// API utilities for Cloudflare Pages with Service Bindings
// Uses relative URLs that are proxied through Pages Functions to the API Worker

class ApiClient {
  constructor() {
    // Primary API endpoint - using custom domain with simple authentication
    this.baseUrl = 'https://api.dimitriu.xyz/api';
    
    // Fallback endpoint (direct Workers URL)
    this.fallbackUrl = 'https://prebid-new-dashboard-api.dimitriusergiu.workers.dev/api';
    
    // Service binding URL for when it's fixed
    this.serviceBindingUrl = '/api';
    
    console.log('[API Client] Initialized with simple authentication mode');
  }

  // Sanitize request body to prevent JSON serialization issues
  sanitizeRequestBody(obj) {
    const seen = new WeakSet();
    
    const sanitize = (value, path = '') => {
      // Handle circular references
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          console.warn(`[API Client] Circular reference detected at ${path}, removing`);
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      
      // Handle undefined values
      if (value === undefined) {
        console.warn(`[API Client] Undefined value at ${path}, converting to null`);
        return null;
      }
      
      // Handle functions
      if (typeof value === 'function') {
        console.warn(`[API Client] Function at ${path}, removing`);
        return '[Function]';
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        return value.map((item, index) => sanitize(item, `${path}[${index}]`));
      }
      
      // Handle objects
      if (typeof value === 'object' && value !== null) {
        const result = {};
        for (const [key, val] of Object.entries(value)) {
          const newPath = path ? `${path}.${key}` : key;
          result[key] = sanitize(val, newPath);
        }
        return result;
      }
      
      // Return primitive values as-is
      return value;
    };
    
    return sanitize(obj);
  }

  async request(endpoint, options = {}) {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Enhanced JSON serialization with validation and logging
    if (config.body && typeof config.body === 'object') {
      try {
        // Validate data before serialization
        console.log(`[API Client] Preparing request to ${endpoint}`);
        
        // Check for circular references and problematic values
        const cleanedBody = this.sanitizeRequestBody(config.body);
        config.body = JSON.stringify(cleanedBody);
        
        console.log(`[API Client] Serialized JSON length: ${config.body.length} chars`);
        
      } catch (serializationError) {
        console.error(`[API Client] JSON serialization failed for ${endpoint}:`, serializationError);
        console.error(`[API Client] Problematic data:`, config.body);
        throw new Error(`Failed to serialize request data: ${serializationError.message}`);
      }
    }

    // Use direct Worker URL (service binding currently broken)
    let response;
    try {
      const url = `${this.baseUrl}${endpoint}`;
      console.log(`[API Client] Requesting: ${url}`);
      response = await fetch(url, config);
    } catch (error) {
      console.error(`[API Client] Request failed:`, error.message);
      throw error;
    }
    
    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401) {
        // Clear session data
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('sessionExpiry');
        localStorage.removeItem('user');
        
        // Reload page to show login modal
        window.location.reload();
        return;
      }
      
      let errorData = null;
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      
      try {
        const errorText = await response.text();
        if (errorText) {
          errorData = JSON.parse(errorText);
          
          // Handle enhanced validation errors
          if (errorData.errors && Array.isArray(errorData.errors)) {
            const validationError = new Error(errorData.summary || 'Validation failed');
            validationError.errors = errorData.errors;
            validationError.summary = errorData.summary;
            validationError.isValidationError = true;
            throw validationError;
          }
          
          // Handle basic error formats
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
      } catch (parseError) {
        // If it's already our enhanced error, re-throw it
        if (parseError.isValidationError) {
          throw parseError;
        }
        // If JSON parsing fails, use the default error message
      }
      
      const apiError = new Error(errorMessage);
      if (errorData) {
        apiError.apiResponse = errorData;
      }
      throw apiError;
    }

    const data = await response.json();
    return data;
  }

  // Sites Management
  async getSites() {
    const response = await this.request('/sites');
    return response.success ? response.sites : [];
  }

  async getSite(siteId) {
    return this.request(`/sites/${siteId}`);
  }

  async createSite(siteData) {
    return this.request('/sites', {
      method: 'POST',
      body: siteData,
    });
  }

  async updateSite(siteId, siteData) {
    return this.request(`/sites/${siteId}`, {
      method: 'PUT',
      body: siteData,
    });
  }

  async deleteSite(siteId) {
    return this.request(`/sites/${siteId}`, {
      method: 'DELETE',
    });
  }

  // Configuration Management
  async getConfig(siteId) {
    const response = await this.request(`/sites/${siteId}/config`);
    return response.success ? response.config : {};
  }

  async updateConfig(siteId, config) {
    return this.request(`/sites/${siteId}/config`, {
      method: 'PUT',
      body: config,
    });
  }

  async updateConfigPartial(siteId, configDiff) {
    return this.request(`/sites/${siteId}/config`, {
      method: 'PATCH',
      body: configDiff,
    });
  }

  // Placements Management
  async getPlacements(siteId) {
    const response = await this.request(`/sites/${siteId}/placements`);
    return response.success ? response.placements : {};
  }

  async updatePlacements(siteId, placements) {
    return this.request(`/sites/${siteId}/placements`, {
      method: 'PUT',
      body: placements,
    });
  }

  async createPlacement(siteId, placementData) {
    return this.request(`/sites/${siteId}/placements`, {
      method: 'POST',
      body: placementData,
    });
  }

  async deletePlacement(siteId, placementId) {
    return this.request(`/sites/${siteId}/placements/${placementId}`, {
      method: 'DELETE',
    });
  }

  // Contexts Management
  async getContexts(siteId) {
    return this.request(`/sites/${siteId}/contexts`);
  }

  async updateContexts(siteId, contexts) {
    return this.request(`/sites/${siteId}/contexts`, {
      method: 'PUT',
      body: contexts,
    });
  }

  // Floor Prices Management
  async getFloors(siteId) {
    return this.request(`/sites/${siteId}/floors`);
  }

  async updateFloors(siteId, floors) {
    return this.request(`/sites/${siteId}/floors`, {
      method: 'PUT',
      body: floors,
    });
  }

  // Deployment Management
  async deploy(siteId, config = {}) {
    return this.request(`/sites/${siteId}/deploy`, {
      method: 'POST',
      body: config,
    });
  }


  async getDeploymentStatus(siteId, deploymentId) {
    return this.request(`/sites/${siteId}/deployments/${deploymentId}`);
  }

  async getDeploymentHistory(siteId) {
    return this.request(`/sites/${siteId}/deployments`);
  }

  // Templates Management
  async getTemplates() {
    return this.request('/templates');
  }

  async getTemplate(templateId) {
    return this.request(`/templates/${templateId}`);
  }

  async createSiteFromTemplate(templateId, siteData) {
    return this.request(`/templates/${templateId}/create-site`, {
      method: 'POST',
      body: siteData,
    });
  }

  // Analytics & Stats
  async getStats(siteId) {
    return this.request(`/sites/${siteId}/stats`);
  }

  async getPerformanceMetrics(siteId, dateRange = '7d') {
    return this.request(`/sites/${siteId}/metrics?range=${dateRange}`);
  }

  // Validation
  async validateConfig(siteId, config) {
    return this.request(`/sites/${siteId}/validate`, {
      method: 'POST',
      body: config,
    });
  }

  async validatePlacement(siteId, placementData) {
    return this.request(`/sites/${siteId}/validate-placement`, {
      method: 'POST',
      body: placementData,
    });
  }

  // Analytics Management
  async fetchAnalytics(endpoint, params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/analytics/${endpoint}${queryParams ? `?${queryParams}` : ''}`;
    return this.request(url);
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;

// Export individual methods for convenience (bound to preserve this context)
export const getSites = (...args) => apiClient.getSites(...args);
export const getSite = (...args) => apiClient.getSite(...args);
export const createSite = (...args) => apiClient.createSite(...args);
export const updateSite = (...args) => apiClient.updateSite(...args);
export const deleteSite = (...args) => apiClient.deleteSite(...args);
export const getConfig = (...args) => apiClient.getConfig(...args);
export const updateConfig = (...args) => apiClient.updateConfig(...args);
export const updateConfigPartial = (...args) => apiClient.updateConfigPartial(...args);
export const getPlacements = (...args) => apiClient.getPlacements(...args);
export const updatePlacements = (...args) => apiClient.updatePlacements(...args);
export const createPlacement = (...args) => apiClient.createPlacement(...args);
export const deletePlacement = (...args) => apiClient.deletePlacement(...args);
export const getContexts = (...args) => apiClient.getContexts(...args);
export const updateContexts = (...args) => apiClient.updateContexts(...args);
export const getFloors = (...args) => apiClient.getFloors(...args);
export const updateFloors = (...args) => apiClient.updateFloors(...args);
export const deploy = (...args) => apiClient.deploy(...args);
export const getDeploymentStatus = (...args) => apiClient.getDeploymentStatus(...args);
export const getDeploymentHistory = (...args) => apiClient.getDeploymentHistory(...args);
export const getTemplates = (...args) => apiClient.getTemplates(...args);
export const getTemplate = (...args) => apiClient.getTemplate(...args);
export const createSiteFromTemplate = (...args) => apiClient.createSiteFromTemplate(...args);
export const getStats = (...args) => apiClient.getStats(...args);
export const getPerformanceMetrics = (...args) => apiClient.getPerformanceMetrics(...args);
export const validateConfig = (...args) => apiClient.validateConfig(...args);
export const validatePlacement = (...args) => apiClient.validatePlacement(...args);

// Helper functions for common operations
export const createBillboardPlacement = async (siteId, name = 'billboard') => {
  const placementData = {
    name,
    selector: `#${name}`,
    contexts: {
      article: {
        enabled: true,
        sizes: {
          desktop: [[728, 90], [970, 250]],
          mobile: [[300, 250], [320, 50]]
        },
        gamPath: `/22264327084/${siteId}_art/${name}`,
        bidderParams: {
          teads: { placementId: 193024 },
          triplelift: { inventoryCode: `${siteId}_${name}_desktop` }
        }
      },
      homepage: {
        enabled: true,
        sizes: {
          desktop: [[970, 250]],
          mobile: [[300, 250]]
        },
        gamPath: `/22264327084/${siteId}_hp/${name}`,
        bidderParams: {
          teads: { placementId: 193024 },
          triplelift: { inventoryCode: `${siteId}_${name}_desktop` }
        }
      }
    },
    settings: {
      lazyLoad: false,
      refresh: false
    }
  };

  return createPlacement(siteId, placementData);
};

export const createSidebarPlacement = async (siteId, name = 'sidebar1') => {
  const placementData = {
    name,
    selector: `#${name}`,
    contexts: {
      article: {
        enabled: true,
        sizes: {
          desktop: [[300, 250], [300, 600]],
          mobile: []
        },
        gamPath: `/22264327084/${siteId}_art/${name}`,
        bidderParams: {
          teads: { placementId: 193024 },
          smartadserver: { formatId: "124650" }
        }
      }
    },
    settings: {
      lazyLoad: false,
      refresh: false,
      deviceTargeting: ["desktop"]
    }
  };

  return createPlacement(siteId, placementData);
};

export const createInArticlePlacement = async (siteId, name = 'inarticle1') => {
  const placementData = {
    name,
    selector: `#${name}`,
    contexts: {
      "1": {  // Use numeric context ID instead of string
        enabled: true,
        sizes: {
          desktop: [[300, 250], [336, 280], [728, 90]],
          mobile: [[300, 250], [336, 280], [320, 480]]
        },
        gamPath: `/22264327084/${siteId}_art/${name}`,
        bidderParams: {
          teads: { placementId: 193025 },
          criteo: { networkId: 11870 }
        }
      }
    },
    settings: {
      lazyLoad: true,
      refresh: false,
      fluid: true
    }
  };

  return createPlacement(siteId, placementData);
};